/**
 * Hook that returns the {Session} total / {Session} only / Full Term
 * subtotal triplet for the active year + session.
 *
 * Reuses the same data the Dashboard already fetches from
 * `/dashboard/stats` — `termCounts` for per-term totals and
 * `fullTermCount` for the cross-term derivation. No new endpoints.
 */
import { useEffect, useState } from "react";
import api from "../api/axios";
import type { SessionLabel } from "../context/SeasonContext";
import type { SessionTotals } from "../utils/sessionTotals";

interface DashboardStatsLite {
  campersThisYear: number;
  fullTermCount: number;
  termCounts: { termId: number; termName: string; count: number }[];
}

interface UseSessionTotalsArgs {
  year: number;
  termId: number | null;
  session: SessionLabel;
}

interface UseSessionTotalsResult {
  totals: SessionTotals | null;
  loading: boolean;
}

export function useSessionTotals({
  year,
  termId,
  session,
}: UseSessionTotalsArgs): UseSessionTotalsResult {
  const [totals, setTotals] = useState<SessionTotals | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const params: Record<string, string> = { year: String(year) };
    if (termId != null) params.termId = String(termId);

    api
      .get<DashboardStatsLite>("/dashboard/stats", { params })
      .then((res) => {
        if (cancelled) return;
        const stats = res.data;
        const termRow = termId != null
          ? stats.termCounts.find((t) => t.termId === termId)
          : null;
        const total = termRow ? termRow.count : stats.campersThisYear;
        const fullTerm =
          session === "rangers" ? 0 : stats.fullTermCount;
        setTotals({
          total,
          fullTerm,
          sessionOnly: Math.max(0, total - fullTerm),
        });
      })
      .catch(() => {
        if (!cancelled) setTotals(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [year, termId, session]);

  return { totals, loading };
}
